import React, { useEffect, useRef, useState } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import GavelIcon from '@mui/icons-material/Gavel';

type CounterProps = {
	label: string;
	target: number;
	suffix?: string;
	duration?: number; // duration in milliseconds
};

export const Counter: React.FC<CounterProps> = ({ label, target, suffix = '', duration = 2000 }) => {
	const [count, setCount] = useState(0);
	const [hasAnimated, setHasAnimated] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting && !hasAnimated) {
					setHasAnimated(true);
					animate();
					observer.unobserve(entry.target); // stop observing after first trigger
				}
			},
			{ threshold: 0.3 } // Start animation when ~30% of the element is visible
		);

		if (ref.current) observer.observe(ref.current);
		return () => observer.disconnect();
	}, [hasAnimated]);

	const animate = () => {
		let startTime: number | null = null;

		const step = (timestamp: number) => {
			if (!startTime) startTime = timestamp;
			const progress = Math.min((timestamp - startTime) / duration, 1);
			const currentCount = Math.floor(progress * target);
			setCount(currentCount);

			if (progress < 1) {
				requestAnimationFrame(step);
			} else {
				setCount(target); // ensure exact final value
			}
		};

		requestAnimationFrame(step);
	};

	return (
		<>
			<Paper
				ref={ref}
				elevation={0}
				sx={{
					p: { xs: 1 },
					borderRadius: 3,
					textAlign: 'center',
					height: '100%',
					background:
						'linear-gradient(180deg, rgba(0,0,0,0.02), rgba(0,0,0,0.04))',
					border: '1px solid rgba(0,0,0,0.06)',
					transform: hasAnimated ? 'translateY(0)' : 'translateY(12px)',
					opacity: hasAnimated ? 1 : 0,
					transition: 'all 600ms ease',
				}}
			>
				<Typography
					variant="h3"
					fontWeight={700}
					sx={{ mb: 1 }}
				>
					{count}
					{suffix}
				</Typography>

				<Typography
					variant="body1"
					color="text.secondary"
					sx={{ fontWeight: 500, letterSpacing: 2.5 }}
				>
					{label}
				</Typography>
			</Paper>
		</>
	);
};